import { ApolloError } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { useMemo } from 'react';

import { ErrorCodes } from '@/types/schema';

interface PublicGraphQLError {
  message: string;
  code: ErrorCodes;
}

type PublicGraphQLErrors = PublicGraphQLError[];

const PUBLIC_ERROR_PROPERTY = 'luminary.code' as const;

/** True for application errors without a transport, protocol, or client failure. */
export function isPureGraphQLError(error: unknown): error is ApolloError {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.length > 0 &&
    !error.networkError &&
    error.protocolErrors.length === 0 &&
    error.clientErrors.length === 0
  );
}

/**
 * Extension key that `annotateGraphQLErrorsLink` adds to each GraphQL error
 * so that error reporters can tell which operation produced the error. The
 * backend does not send this key.
 */
export const OPERATION_NAME_EXTENSION = 'luminary.operationName' as const;

/**
 * Shape of the `luminary.code` extension that the backend attaches to every
 * GraphQL error. See `errs.Code` in the platform repo.
 */
export interface LuminaryErrorCode {
  display: string;
  status_code: number;
}

/**
 * @description Returns the backend error code attached to a GraphQL error, or
 * undefined when the extension is missing or malformed.
 */
export function getLuminaryErrorCode(
  graphQLError: GraphQLFormattedError
): LuminaryErrorCode | undefined {
  const code = graphQLError.extensions?.[PUBLIC_ERROR_PROPERTY];
  if (!code || typeof code !== 'object') return undefined;
  if (!('display' in code) || typeof code.display !== 'string') {
    return undefined;
  }
  const statusCode =
    'status_code' in code && typeof code.status_code === 'number'
      ? code.status_code
      : 0;
  return { display: code.display, status_code: statusCode };
}

/**
 * @description Returns the operation name that `annotateGraphQLErrorsLink`
 * stamped on the error, or undefined when the error did not pass through the
 * Apollo link chain.
 */
export function getGraphQLErrorOperationName(
  graphQLError: GraphQLFormattedError
): string | undefined {
  const operationName = graphQLError.extensions?.[OPERATION_NAME_EXTENSION];
  return typeof operationName === 'string' ? operationName : undefined;
}

export interface GraphQLValidationIssue {
  message: string;
  path: string;
  code?: string;
  fieldId?: string;
}

export function useGraphQLErrors(
  error: ApolloError | undefined
): PublicGraphQLErrors {
  return useMemo(() => {
    if (!error) return [];

    const errors: PublicGraphQLErrors = [];

    error.graphQLErrors.forEach((graphQLError) => {
      const publicDisplayCode = getLuminaryErrorCode(graphQLError)?.display;
      if (!publicDisplayCode) return;

      // if this error code isn't in the ErrorCodes GraphQL shared type, do nothing
      if (
        Object.values<string>(ErrorCodes).includes(publicDisplayCode) === false
      ) {
        return;
      }

      errors.push({
        message: graphQLError.message,
        code: publicDisplayCode as ErrorCodes,
      });
    });

    return errors;
  }, [error]);
}

export function useSpecificGraphQLError(
  errors: PublicGraphQLErrors,
  errorToFind: ErrorCodes
): PublicGraphQLError | undefined {
  return useMemo(() => {
    if (!errors) return undefined;
    if (errors?.length === 0) return undefined;

    return errors.find((error) => {
      return error.code === errorToFind;
    });
  }, [errors, errorToFind]);
}

/**
 * @description Given an ApolloError, returns a list of user-facing error messages
 * sent from the backend
 */
export function getUserFacingErrorMessages(error: ApolloError): string[] {
  const userFacingErrors = getGraphQLErrorsByStatusCode(
    error,
    GraphQLStatusCodes.BAD_INPUT
  );
  return userFacingErrors.map((e) => {
    return e.message;
  });
}

export function getGraphQLValidationIssues(
  error: unknown
): GraphQLValidationIssue[] {
  if (
    !error ||
    typeof error !== 'object' ||
    !('graphQLErrors' in error) ||
    !Array.isArray(error.graphQLErrors)
  )
    return [];
  return error.graphQLErrors.flatMap((graphQLError) => {
    if (
      !graphQLError ||
      typeof graphQLError !== 'object' ||
      !('extensions' in graphQLError) ||
      !graphQLError.extensions ||
      typeof graphQLError.extensions !== 'object'
    )
      return [];
    const issues =
      'validationIssues' in graphQLError.extensions
        ? graphQLError.extensions.validationIssues
        : undefined;
    if (!Array.isArray(issues)) return [];

    return issues.flatMap((issue: unknown) => {
      if (
        !issue ||
        typeof issue !== 'object' ||
        !('message' in issue) ||
        typeof issue.message !== 'string'
      ) {
        return [];
      }

      const path =
        'path' in issue && typeof issue.path === 'string' ? issue.path : '$';
      const code =
        'code' in issue && typeof issue.code === 'string'
          ? { code: issue.code }
          : {};
      const fieldId =
        'fieldId' in issue && typeof issue.fieldId === 'string'
          ? { fieldId: issue.fieldId }
          : {};
      return [{ message: issue.message, path, ...code, ...fieldId }];
    });
  });
}

export enum GraphQLStatusCodes {
  BAD_INPUT = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
}

/** Matches structured auth errors without relying on error message text. */
export function isGraphQLAuthError(error: GraphQLFormattedError): boolean {
  const code = getLuminaryErrorCode(error);
  return (
    code?.status_code === GraphQLStatusCodes.UNAUTHORIZED ||
    code?.status_code === GraphQLStatusCodes.FORBIDDEN
  );
}

/**
 * Common GraphQL subscription error messages
 */
export const GraphQLSubscriptionErrors = {
  RECONNECT_MESSAGE: 'reconnect subscription',
} as const;

/**
 * @description Returns true when a subscription error is the backend's routine
 * "reconnect subscription" signal, sent when the server rotates a long-lived
 * subscription (each one is capped at a fixed timeout). Callers should restart
 * the subscription rather than surface a failure to the user.
 */
export function isReconnectSubscriptionError(error: ApolloError): boolean {
  return (
    error.message.includes(GraphQLSubscriptionErrors.RECONNECT_MESSAGE) ||
    error.graphQLErrors.some((graphQLError) =>
      graphQLError.message.includes(GraphQLSubscriptionErrors.RECONNECT_MESSAGE)
    )
  );
}

/**
 * @description Returns true when every GraphQL error on an ApolloError is a
 * not-found error, i.e. the request itself was well-formed and the referenced
 * object simply doesn't exist (e.g. it was just deleted). Callers that treat a
 * missing object as an expected outcome can use this to skip error reporting.
 */
export function isNotFoundError(error: ApolloError): boolean {
  const graphQLErrors = Array.from(error.graphQLErrors);
  if (graphQLErrors.length === 0) {
    return false;
  }

  return (
    getGraphQLErrorsByStatusCode(error, GraphQLStatusCodes.NOT_FOUND).length ===
    graphQLErrors.length
  );
}

/**
 * @description Given an ApolloError, returns a list of GraphQLFormattedError objects
 * that are associated with the BAD_INPUT error code
 */
export function getGraphQLErrorsByStatusCode(
  error: ApolloError,
  statusCode: GraphQLStatusCodes
): GraphQLFormattedError[] {
  return Array.from(error.graphQLErrors).filter((graphQLError) => {
    return getLuminaryErrorCode(graphQLError)?.status_code === statusCode;
  });
}
